import React, { useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem,
    Select, Switch, TextField, Grid
} from '@material-ui/core';
import { AdminTemplateRequest } from 'api/whatsapp/types';
import { IApiState } from 'store/types';
import { apiStatus } from 'shared/constants/AppEnum';

interface TemplateFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: AdminTemplateRequest) => void;
    api: IApiState;
    initialData?: AdminTemplateRequest & { id?: number };
    mode: 'create' | 'edit';
}

const CATEGORIES = [
    { value: 'offering_reminder', label: 'Offering Reminder' },
    { value: 'order_info_multiple', label: 'Order Info (Multiple)' },
    { value: 'donation_info', label: 'Donation Info' },
    { value: 'offering_completion', label: 'Offering Completion' },
    { value: 'payment_reminder', label: 'Payment Reminder' },
    { value: 'hall_booking_confirmation', label: 'Booking Confirmation' },
    { value: 'consolidation_info', label: 'Consolidation Info' },
    { value: 'voucher_info', label: 'Voucher Info' },
];

const HEADER_TYPES = [
    { value: 'none', label: 'None' },
    { value: 'text', label: 'Text' },
    { value: 'image', label: 'Image' },
    { value: 'document', label: 'Document' },
    { value: 'video', label: 'Video' },
];

const defaultValues: AdminTemplateRequest = {
    template_name: '',
    category: '',
    description: '',
    language: 'en',
    header_type: 'none',
    has_buttons: false,
    body_template: '',
    is_active: true,
    is_default: false,
    notes: '',
};

const TemplateFormDialog: React.FC<TemplateFormDialogProps> = ({
    open, onClose, onSubmit, api, initialData, mode
}) => {
    const [form, setForm] = useState<AdminTemplateRequest>(initialData || defaultValues);
    const isLoading = api.status === apiStatus.LOADING;

    React.useEffect(() => {
        if (open) {
            setForm(initialData || defaultValues);
        }
    }, [open, initialData]);

    const handleChange = (field: keyof AdminTemplateRequest, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = () => {
        onSubmit(form);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{mode === 'create' ? 'Create Template' : 'Edit Template'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            label="Template Name"
                            value={form.template_name}
                            onChange={e => handleChange('template_name', e.target.value)}
                            fullWidth
                            required
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth margin="dense" required>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={form.category}
                                onChange={e => handleChange('category', e.target.value)}
                            >
                                {CATEGORIES.map(cat => (
                                    <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Language"
                            value={form.language ?? 'en'}
                            onChange={e => handleChange('language', e.target.value)}
                            fullWidth
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Header Type</InputLabel>
                            <Select
                                value={form.header_type ?? 'none'}
                                onChange={e => handleChange('header_type', e.target.value)}
                            >
                                {HEADER_TYPES.map(ht => (
                                    <MenuItem key={ht.value} value={ht.value}>{ht.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            value={form.description ?? ''}
                            onChange={e => handleChange('description', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Body Template"
                            value={form.body_template ?? ''}
                            onChange={e => handleChange('body_template', e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            margin="dense"
                            helperText="Use {{1}}, {{2}}, etc. for placeholders"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            value={form.notes ?? ''}
                            onChange={e => handleChange('notes', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            margin="dense"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_active ?? true}
                                    onChange={e => handleChange('is_active', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_default ?? false}
                                    onChange={e => handleChange('is_default', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Default"
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.has_buttons ?? false}
                                    onChange={e => handleChange('has_buttons', e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Has Buttons"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isLoading || !form.template_name || !form.category}
                    startIcon={isLoading ? <CircularProgress size={14} /> : undefined}
                >
                    {mode === 'create' ? 'Create' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplateFormDialog;
